import { UseFormReturn } from 'react-hook-form'
import { CartConditionStateType, CartConditionErrorStateType } from './common/cartConditions'
import {
  ActivePeriodErrorStateType,
  ActivePeriodStateType,
  CustomerEligibilityStateType
} from './common/customerConditions'
import { FlowErrorType } from './common/error'
import { PaymentMethodType } from './dashboardAnalytics'

export type AutomationSortOrderType = 'NEWEST' | 'OLDEST' | 'NAME_ASC' | 'NAME_DESC'

export enum AutomationStatus {
  ACTIVE = 'ACTIVE',
  COMING_SOON = 'COMING_SOON'
}

export type AutomationDiscountAppplyCondition = {
  discountId: string
  discountCode: string
}

export enum ExecutableConditionType {
  ON_APPLY_REWARDS = 'ON_APPLY_REWARDS',
  ON_APPLY_DISCOUNTS = 'ON_APPLY_DISCOUNTS'
}

export enum DisabledEntityType {
  DISABLE_PAYMENT = 'DISABLE_PAYMENT',
  DISABLE_REWARDS = 'DISABLE_REWARDS',
  NONE = 'NONE'
}

export type AutomationApplicableConditions = {
  type: ExecutableConditionType
  count: number
  conditions: AutomationDiscountAppplyCondition[]
}

export type AutomationType = {
  id: string
  title: string
  description: string
  type: DisabledEntityType
  disabledPaymentModes: PaymentMethodType[]
  activePaymentModes: PaymentMethodType[]
  isActive: boolean
  applicableConditions?: AutomationApplicableConditions
  createdAt?: string
}

export type ExecutableCondition = {
  type: ExecutableConditionType
  entity_ids: string[]
  metadata: any
}

export type AutomationConfig = {
  name: string
  desription: string
  type: DisabledEntityType
  disabledEntities: PaymentMethodType[]
  merchantEnabledEntities: PaymentMethodType[]
  isActive: boolean
  executableConditions: ExecutableCondition[]
}

export interface AutomationContextType {
  state: AutomationStateType
  form: UseFormReturn<AutomationStateType>
  actions: {
    handleSave: () => Promise<void>
  }
}

export enum AutomationActionSet {
  SET_TITLE,
  SET_DESCRIPTION,
  SET_CART_CONDITIONS,
  SET_CUSTOMER_ELIGIBILITY,
  SET_ACTIVE_PERIOD,
  SET_AUTOMATED_ACTION,
  SET_PERFORMED_ACTION,
  SET_JOURNEY_STEP,
  SET_AUTOMATION,
  SET_IS_ACTIVE,
  SET_CHANNEL,
  SET_ERROR_STATE
}

export type AutomationActionType =
  | {
      type: AutomationActionSet.SET_TITLE
      payload: string
    }
  | {
      type: AutomationActionSet.SET_DESCRIPTION
      payload: string
    }
  | {
      type: AutomationActionSet.SET_CART_CONDITIONS
      payload: CartConditionStateType
    }
  | {
      type: AutomationActionSet.SET_CUSTOMER_ELIGIBILITY
      payload: CustomerEligibilityStateType
    }
  | {
      type: AutomationActionSet.SET_ACTIVE_PERIOD
      payload: ActivePeriodStateType
    }
  | {
      type: AutomationActionSet.SET_AUTOMATED_ACTION
      payload: AutomatedActionType
    }
  | {
      type: AutomationActionSet.SET_PERFORMED_ACTION
      payload: PerformedActionType
    }
  | {
      type: AutomationActionSet.SET_AUTOMATION
      payload: AutomationStateType
    }
  | {
      type: AutomationActionSet.SET_IS_ACTIVE
      payload: boolean
    }
  | {
      type: AutomationActionSet.SET_CHANNEL
      payload: string[]
    }

export type AutomationStateType = {
  title: string
  description?: string
  automatedAction: AutomatedActionType
  performedAction: PerformedActionType
  cartConditions: CartConditionStateType
  customerEligibility: CustomerEligibilityStateType
  activePeriod: ActivePeriodStateType
  isActive: boolean
  channelIds: string[]
}

export enum AutomatedActions {
  DISABLE_REWARDS = 'DISABLE_REWARDS',
  HIDE_PAYMENT_MODES = 'HIDE_PAYMENT_MODES',
  NONE = 'NONE'
}

export type AutomatedActionType =
  | {
      type: AutomatedActions.NONE
    }
  | {
      type: AutomatedActions.DISABLE_REWARDS
    }
  | {
      type: AutomatedActions.HIDE_PAYMENT_MODES
      paymentMethodIds: string[]
    }

export enum PerformedActionsOptions {
  NO_CONDITIONS = 'NO_CONDITIONS',
  REDEEM_REWARDS = 'REDEEM_REWARDS',
  APPLY_DISCOUNTS = 'APPLY_DISCOUNTS'
}

export type PerformedActionType =
  | {
      type: PerformedActionsOptions.NO_CONDITIONS
    }
  | {
      type: PerformedActionsOptions.REDEEM_REWARDS
    }
  | {
      type: PerformedActionsOptions.APPLY_DISCOUNTS
      appliedDiscounts: string[]
      discountsMetadata: AutomationDiscountDataType[]
    }

export enum AutomationFlowJourneySteps {
  SETUP = 'setup',
  CART_CONDITIONS = 'cart_conditions',
  CUSTOMER_ELIGIBILITY = 'customer_eligibility',
  USAGE_LIMITS = 'usage_limits'
}

export type AutomatedActionsSelectionType = {
  label: string
  value: AutomatedActions
}

export type MerchantPaymentMode = {
  mode: string
  title: string
  pgName: string
}

export type AutomationDiscountDataType = {
  couponId: string
  id: string
  code: string
  title: string
  isActive: boolean
}

export type AutomationTemplateType = {
  id: string
  name: string
  title: string
  description: string
  image: string
  automation: {
    type: DisabledEntityType
    label: string
  }
}
export type AutomationBulkResponse = {
  success: boolean
  data: {
    data: any[]
    total_count: number
    current_page: number
    page_size: number | null
    max_pages: number
  }
}

export interface AutomationLandingPageLayoutProps {
  children: React.ReactNode
  search: {
    searchText: string
    setSearchText: (text: string) => void
  }
  sort: {
    sortOrder: AutomationSortOrderType
    setSortOrder: (order: AutomationSortOrderType) => void
  }
  setIsCreateAutomationModalOpen: (open: boolean) => void
}

export interface AutomationTypeTableProps {
  type: string
  searchText: string
  sortOrder: AutomationSortOrderType
}

export interface ViewAutomationsTabProps {
  searchText: string
  sortOrder: AutomationSortOrderType
}
